import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, ChevronUp, ChevronLeft, ChevronRight } from 'lucide-react';
import type { ApiMetadata } from '../../hooks/useCoaching';
import { HelpTooltip, HoverHelpTooltip } from '../ui/HelpTooltip';

/**
 * Centered tooltip overlay for displaying full coaching pointer text
 * Uses same positioning logic as HelpTooltip:
 * - Horizontally centered relative to main content area (excluding sidebar)
 * - Vertically: centered if possible, otherwise top at 25vh
 * Uses React Portal to render at document body level, escaping any parent CSS transforms
 */
interface CoachingTooltipOverlayProps {
  text: string;
  onClose: () => void;
}

const CoachingTooltipOverlay: React.FC<CoachingTooltipOverlayProps> = ({ text, onClose }) => {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ left: number; top: number } | null>(null);

  // Calculate positioning after render
  useEffect(() => {
    if (!tooltipRef.current) return;
    
    const tooltipHeight = tooltipRef.current.offsetHeight;
    const tooltipWidth = tooltipRef.current.offsetWidth;
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const quarterFold = viewportHeight * 0.25;
    
    // Detect sidebar width dynamically
    let sidebarWidth = 0;
    const sidebar = document.querySelector('aside.sidebar');
    if (sidebar) {
      const sidebarRect = sidebar.getBoundingClientRect();
      // Only count sidebar width if it's visible (not translated off-screen)
      if (sidebarRect.right > 0) {
        sidebarWidth = sidebarRect.width;
      }
    }
    
    // On mobile (< 1024px / lg breakpoint), sidebar is overlay, so don't offset
    const isDesktop = viewportWidth >= 1024;
    const effectiveSidebarWidth = isDesktop ? sidebarWidth : 0;
    
    // Calculate horizontal center of content area (viewport minus sidebar)
    const contentWidth = viewportWidth - effectiveSidebarWidth;
    const contentCenterX = effectiveSidebarWidth + (contentWidth / 2);
    const left = contentCenterX - (tooltipWidth / 2);
    
    // Calculate vertical position
    // Try to center, but ensure top is at or above 25vh (not below)
    const centeredTop = (viewportHeight / 2) - (tooltipHeight / 2);
    const top = Math.min(centeredTop, quarterFold);
    
    setPosition({ left, top });
  }, [text]);

  // Close on click anywhere or Escape key
  useEffect(() => {
    const handleClickAnywhere = () => onClose();
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    // Small delay to prevent immediate close from the triggering click
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickAnywhere);
      document.addEventListener('keydown', handleKeyDown);
    }, 10);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickAnywhere);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  const tooltipElement = (
    <div
      ref={tooltipRef}
      role="tooltip"
      style={position ? { left: position.left, top: position.top } : undefined}
      className={`
        fixed z-[9999]
        w-[85vw] max-w-md
        px-4 py-3
        bg-popover text-popover-foreground
        text-sm leading-relaxed text-left
        rounded-lg shadow-xl
        border border-border
        animate-in fade-in-0 zoom-in-95 duration-150
        whitespace-pre-line
        pointer-events-none
        max-h-[65vh] overflow-y-auto
        ${!position ? 'opacity-0' : ''}
      `}
    >
      {text}
    </div>
  );

  // Use portal to render at document body level, escaping any parent transforms
  return createPortal(tooltipElement, document.body);
};

interface CoachingLineProps {
  /** The coaching pointer text to display */
  pointer: string | null;
  /** The full prompt that was sent to the API */
  fullPrompt: string | null;
  /** API metadata (endpoint, model, config) */
  apiMetadata: ApiMetadata | null;
  /** Whether coaching is currently being generated */
  isLoading: boolean;
  /** Whether coaching is enabled for the user */
  isEnabled: boolean;
  /** Callback to toggle coaching on/off */
  onToggle: () => void;
  /** Navigate to previous pointer */
  onPrevious: () => void;
  /** Navigate to next pointer */
  onNext: () => void;
  /** Whether there's a previous pointer */
  hasPrevious: boolean;
  /** Whether there's a next pointer */
  hasNext: boolean;
  /** Whether to show the help questionmark */
  showHelp?: boolean;
  /** Help text for the coaching row questionmark */
  helpText?: string;
  /** Callback when help is viewed (questionmark dismissed) */
  onHelpViewed?: () => void;
  /** Production mode - hides API details dropdown */
  prodMode?: boolean;
}

/**
 * CoachingLine - Displays coaching pointers above the prompts line in the status section
 * 
 * Features:
 * - Single line display with truncation
 * - Clickable "..." for expanding truncated text
 * - Toggle button to enable/disable coaching
 * - Chevron to show/hide the full API prompt
 * - Back/forward navigation through coaching history
 * - Smooth animations for loading and appearance
 */
/**
 * Lowercase only the first character of a string, preserving other capitalization (e.g., "AI")
 */
function lowercaseFirstChar(str: string): string {
  if (!str || str.length === 0) return str;
  return str.charAt(0).toLowerCase() + str.slice(1);
}

export const CoachingLine: React.FC<CoachingLineProps> = ({
  pointer,
  fullPrompt,
  apiMetadata,
  isLoading,
  isEnabled,
  onToggle,
  onPrevious,
  onNext,
  hasPrevious,
  hasNext,
  showHelp = false,
  helpText = '',
  onHelpViewed,
  prodMode = false,
}) => {
  // Debug: Log received props
  useEffect(() => {
    console.log('🎯 CoachingLine props:', {
      pointer: pointer ? `"${pointer.substring(0, 30)}..."` : null,
      fullPrompt: fullPrompt ? `(${fullPrompt.length} chars)` : null,
      apiMetadata: apiMetadata ? { model: apiMetadata.model, provider: apiMetadata.provider } : null,
      isLoading,
      isEnabled,
      hasPrevious,
      hasNext,
    });
  }, [pointer, fullPrompt, apiMetadata, isLoading, isEnabled, hasPrevious, hasNext]);

  const [isExpanded, setIsExpanded] = useState(false);
  const [isPromptExpanded, setIsPromptExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const textRef = useRef<HTMLSpanElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  
  // Check if text is truncated
  useEffect(() => {
    if (textRef.current && pointer) {
      const isOverflowing = textRef.current.scrollWidth > textRef.current.clientWidth;
      setIsTruncated(isOverflowing);
    } else {
      setIsTruncated(false);
    }
  }, [pointer]);
  
  // Close expanded view when clicking outside
  useEffect(() => {
    if (!isExpanded && !isPromptExpanded) return;
    
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsExpanded(false);
        setIsPromptExpanded(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isExpanded, isPromptExpanded]);
  
  // Toggle button styling - matches existing toggle pattern
  const toggleButtonClass = `
    inline-flex items-center justify-center px-2 h-6 rounded text-xs font-medium
    transition-all duration-200 cursor-pointer hover:scale-105 bg-gray-600
    ${isEnabled
      ? 'text-white border border-white/70 dark:border-white/70'
      : 'text-gray-400 border border-transparent'
    }
  `;

  // Chevron button styling for API prompt dropdown
  const promptChevronClass = `
    inline-flex items-center justify-center w-5 h-6 rounded text-xs
    transition-all duration-200 cursor-pointer hover:bg-gray-700
    text-muted-foreground hover:text-foreground
    ${!fullPrompt ? 'opacity-30 cursor-not-allowed' : ''}
  `;

  // Navigation chevron styling
  const navChevronClass = (enabled: boolean) => `
    inline-flex items-center justify-center w-6 h-6 rounded text-xs
    transition-all duration-200
    ${enabled
      ? 'cursor-pointer hover:bg-gray-700 text-muted-foreground hover:text-foreground'
      : 'opacity-30 cursor-not-allowed text-muted-foreground/30'
    }
  `;
  
  return (
    <HoverHelpTooltip
      helpText={helpText}
      enabled={!showHelp && !!helpText}
    >
    <div 
      ref={containerRef}
      className="relative flex flex-col items-center gap-1 text-sm text-muted-foreground min-h-[24px]"
    >
      {/* Main coaching line */}
      <div className="flex items-center justify-center gap-1 max-w-full overflow-hidden">
        {/* Left navigation chevron */}
        <button
          onClick={() => hasPrevious && onPrevious()}
          className={navChevronClass(hasPrevious)}
          title={hasPrevious ? 'Previous coaching pointer' : 'No previous pointer'}
          disabled={!hasPrevious}
        >
          <ChevronLeft className="w-4 h-4" />
        </button>

        {/* Toggle button */}
        <button
          onClick={onToggle}
          className={`${toggleButtonClass} flex-shrink-0`}
          title={isEnabled ? 'Disable coaching' : 'Enable coaching'}
        >
          icoach
        </button>

        {/* Chevron to expand/collapse API prompt - hidden in prodMode */}
        {!prodMode && (
          <button
            onClick={() => fullPrompt && setIsPromptExpanded(!isPromptExpanded)}
            className={promptChevronClass}
            title={fullPrompt ? (isPromptExpanded ? 'Hide API prompt' : 'Show API prompt') : 'No prompt available'}
            disabled={!fullPrompt}
          >
            {isPromptExpanded ? (
              <ChevronUp className="w-3.5 h-3.5" />
            ) : (
              <ChevronDown className="w-3.5 h-3.5" />
            )}
          </button>
        )}
        
        {/* Coaching text area - click anywhere to expand */}
        <div className="flex items-center gap-1 overflow-hidden">
          {pointer ? (
            <span
              ref={textRef}
              onClick={() => {
                if (isTruncated) {
                  setIsExpanded(!isExpanded);
                  // Mark as interacted when expanding
                  if (onHelpViewed) onHelpViewed();
                }
              }}
              className={`
                truncate
                transition-all duration-200
                ${isTruncated ? 'cursor-pointer hover:text-foreground' : ''}
              `}
              title={isTruncated ? 'Click to show full message' : undefined}
            >
              {lowercaseFirstChar(pointer)}
            </span>
          ) : null}
        </div>

        {/* Right navigation chevron */}
        <button
          onClick={hasNext ? onNext : undefined}
          className={navChevronClass(hasNext)}
          title={hasNext ? 'Next coaching pointer' : 'No next pointer'}
          disabled={!hasNext}
        >
          <ChevronRight className="w-4 h-4" />
        </button>

        {/* Help questionmark for coaching row */}
        {showHelp && helpText && (
          <HelpTooltip
            helpText={helpText}
            onHelpViewed={onHelpViewed}
            size="sm"
          />
        )}
      </div>

      {/* API Prompt dropdown - hidden in prodMode */}
      {!prodMode && isPromptExpanded && fullPrompt && (
        <div 
          className="
            w-full max-w-4xl mt-1 z-40
            bg-zinc-900 text-zinc-300
            text-xs leading-relaxed font-mono
            rounded-md shadow-lg
            border border-zinc-700
            animate-in fade-in-0 slide-in-from-top-2 duration-150
            max-h-[50vh] overflow-auto
          "
        >
          <div className="sticky top-0 flex items-center justify-between px-3 py-2 bg-zinc-800 border-b border-zinc-700">
            <span className="text-zinc-400 font-sans font-medium">API Call Details</span>
            <button
              onClick={() => setIsPromptExpanded(false)}
              className="text-zinc-500 hover:text-zinc-300 text-sm font-sans"
            >
              ✕
            </button>
          </div>
          
          {/* API Metadata Section */}
          {apiMetadata && (
            <div className="px-3 py-2 bg-zinc-850 border-b border-zinc-700 space-y-1">
              <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
                <span className="text-zinc-500">Endpoint:</span>
                <span className="text-emerald-400 break-all">{apiMetadata.endpoint}</span>
                
                <span className="text-zinc-500">Model:</span>
                <span className="text-cyan-400">{apiMetadata.model}</span>
                
                <span className="text-zinc-500">Provider:</span>
                <span className="text-amber-400">{apiMetadata.provider}</span>
                
                <span className="text-zinc-500">Temperature:</span>
                <span className="text-zinc-300">{apiMetadata.temperature}</span>
                
                <span className="text-zinc-500">Max Tokens:</span>
                <span className="text-zinc-300">{apiMetadata.maxOutputTokens ?? 'default'}</span>
                
                <span className="text-zinc-500">Cost:</span>
                <span className="text-zinc-300">${apiMetadata.costUSD?.toFixed(6) || '0.000000'}</span>
                
                <span className="text-zinc-500">Timestamp:</span>
                <span className="text-zinc-400">{apiMetadata.timestamp}</span>
              </div>
            </div>
          )}
          
          {/* Prompt Content */}
          <div className="px-3 py-2 border-b border-zinc-700 bg-zinc-800/50">
            <span className="text-zinc-400 font-sans font-medium text-[11px] uppercase tracking-wide">Prompt Sent</span>
          </div>
          <pre className="p-3 whitespace-pre-wrap break-words">
            {fullPrompt}
          </pre>
        </div>
      )}
      
      {/* Expanded pointer tooltip overlay - centered on screen (same as help tooltips) */}
      {isExpanded && pointer && !isPromptExpanded && (
        <CoachingTooltipOverlay 
          text={lowercaseFirstChar(pointer)} 
          onClose={() => setIsExpanded(false)} 
        />
      )}
    </div>
    </HoverHelpTooltip>
  );
};

export default CoachingLine;
